import { useTenant } from "@/providers/session";
import { type TypedDocumentNode, gql, useQuery } from "@apollo/client";

export interface CompanyAddress {
  addressId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export enum LegalForm {
  SOLE_PROPRIETORSHIP = "SOLE_PROPRIETORSHIP",
  FREELANCER = "FREELANCER",
  REGISTERED_MERCHANT = "REGISTERED_MERCHANT",
  CIVIL_LAW_PARTNERSHIP = "CIVIL_LAW_PARTNERSHIP",
  GENERAL_PARTNERSHIP = "GENERAL_PARTNERSHIP",
  ENTREPRENEURIAL_COMPANY = "ENTREPRENEURIAL_COMPANY",
  LIMITED_LIABILITY_COMPANY = "LIMITED_LIABILITY_COMPANY",
  LIMITED_PARTNERSHIP = "LIMITED_PARTNERSHIP",
  LIBERAL_PROFESSIONS_PARTNERSHIP = "LIBERAL_PROFESSIONS_PARTNERSHIP",
  PUBLIC_LIMITED_COMPANY = "PUBLIC_LIMITED_COMPANY",
  LIMITED_PARTNERSHIP_GMBH = "LIMITED_PARTNERSHIP_GMBH",
  OTHER = "OTHER",
}

type CompanyWithAddressesQuery = {
  company: {
    companyId: string;
    companyName: string;
    vatId: string;
    thirteenBType: "CONSTRUCTION" | "STANDARD" | "CLEANING";
    fiscalYearStartDay: number;
    fiscalYearStartMonth: number;
    addresses: CompanyAddress[];
    legalForm: LegalForm;
    noGeneratedBookingTextInExportPayables?: boolean;
    noGeneratedBookingTextInExportReceivables?: boolean;
    useCustomerNumberAsDebtorAccount?: boolean;
    creditorAccountStart?: string;
  };
};

const COMPANY_QUERY_WITH_ADDRESSES: TypedDocumentNode<CompanyWithAddressesQuery> = gql`
  query CompanyWithAddresses($companyId: String!) {
    company(companyId: $companyId) {
      companyId
      companyName
      vatId
      thirteenBType
      fiscalYearStartDay
      fiscalYearStartMonth
      addresses {
        addressId
        addressLine1
        addressLine2
        city
        state
        postcode
        country
      }
      legalForm
      noGeneratedBookingTextInExportPayables
      noGeneratedBookingTextInExportReceivables
      useCustomerNumberAsDebtorAccount
      creditorAccountStart
    }
  }
`;

export const useGetCompany = () => {
  const { tenantId } = useTenant();
  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
    refetch: refetchCompany,
  } = useQuery(COMPANY_QUERY_WITH_ADDRESSES, {
    variables: { companyId: tenantId },
    skip: !tenantId,
  });

  return {
    companyData,
    companyLoading,
    companyError,
    refetchCompany,
  };
};
